/**
 * The Meet Mia reel's content (GIFTWEB-28, rewritten for GIFTWEB-39 item 12).
 *
 * COMPLIANCE — every figure and every regulated sentence below is lifted from
 * Djuro's brief of 2 Sep 2026, which supersedes the August design handoff in
 * `.claude/AlexGifting/handoff-intro-reel/` that the first build was ported
 * from. The reel is not a video and never was: it is four still photographs
 * with copy and motion layered over them, which is why nothing could be
 * "converted" to match the app. Everything the client can read is gathered
 * here so a correction is one edit rather than a hunt through markup.
 *
 * Two deliberate departures from the reference recording, both agreed:
 *   - Beat 1's headline is the new "A gift to last a lifetime". The recording
 *     still shows the old "Gift a child a brighter future"; the change had not
 *     been merged when it was made.
 *   - Beat 1's trust row and beat 3's disclaimer follow the brief's wording
 *     where the recording differs. The recording reads "Your money is managed
 *     by" with two logos, and "no guarantee for returns".
 *
 */

export const REEL_FIGURES = {
  /**
   * Beat 3. Low and high are exact figures and must be rendered verbatim —
   * the medium is the only one that counts up, and only it is rounded to
   * hundreds while rolling.
   */
  projectionFrom: 2_000,
  projectionLow: 12_987,
  projectionMedium: 17_500,
  projectionHigh: 24_004,

  monthlyContribution: '£50',
  years: 18,
  /** The band, not a single rate — the amount screen's 3/5/7 is not this. */
  growthRates: '2%/5%/8%',
} as const;

/**
 * Beat 3's footnote, split so the risk warning can be emphasised. The bold is
 * a compliance requirement, not styling — Djuro was explicit about that — so
 * the two halves are separate strings rather than one with markup in it.
 */
export const PROJECTION_DISCLAIMER_BODY =
  `*Illustration based on ${REEL_FIGURES.monthlyContribution} a month for ` +
  `${REEL_FIGURES.years} years at ${REEL_FIGURES.growthRates} average annual ` +
  `growth. This projection is gross of fees and inflation and no guarantee of return.`;

export const PROJECTION_DISCLAIMER_RISK = 'Your capital is at risk.';

/**
 * Beat 4's gift cards, in the order they stack in.
 *
 * NOTE the reference recording draws Nana Jean's avatar as "JP", while the two
 * above it match their names. Read as a slip in the app rather than a
 * deliberate choice, so this uses her initials; one edit here if Djuro says
 * otherwise.
 */
export const REEL_GIFTS = [
  { initials: 'GA', name: 'Grandpa Adam', amount: '£20', bg: '#eadfc4', fg: '#9c7a3c' },
  { initials: 'EM', name: 'Auntie Em', amount: '£15', bg: '#d8e6f2', fg: '#3f6f96' },
  { initials: 'NJ', name: 'Nana Jean', amount: '£50', bg: '#f4dae6', fg: '#96416a' },
] as const;

export const REEL_COPY = {
  /** Carried across every beat, beside the animated wordmark. */
  greeting: "Hi, I'm",
  beat1: {
    headline: 'A gift to last a lifetime',
    chips: ['Junior Stocks & Shares ISA', 'Junior Pension'],
    trust: 'Protected & FCA-regulated',
  },
  beat2: {
    lead: 'Together we can save',
    /** The emphasised word — the whole point of the beat. */
    emphasis: 'millions',
    tail: 'of toys from landfill',
  },
  beat3: {
    lead: `investing ${REEL_FIGURES.monthlyContribution}pm instead could grow to`,
    tagline: 'a real head start, not landfill*',
  },
  beat4: {
    headline: 'Every gift carries a memory',
    voiceFrom: 'Voice message from Auntie Olivia',
    voiceDuration: '0:22',
    message: 'Happy birthday, gorgeous girl! 🎂 Love, Auntie Em x',
    unlock: 'Can be unlocked on their 18th birthday.',
  },
} as const;

/**
 * Beat durations in milliseconds — 22.4s in total.
 *
 * Measured off the reference recording rather than carried over on trust: its
 * beats change at 4.0s, 9.4s and 14.4s, and the reel gives way to the app at
 * 22.53s. Unchanged from the August handoff.
 */
export const BEAT_DURATIONS_MS: Record<number, number> = {
  1: 4_000,
  2: 5_400,
  3: 5_000,
  4: 8_000,
};

/**
 * How long into beat 4 the written message replaces the typing bubble, timed
 * off the reference. Here with the other durations rather than buried in the
 * component, so a test can shorten it the way it shortens the beats.
 */
export const MESSAGE_DELAY_MS = 3_200;

export const REEL_TOTAL_MS = Object.values(BEAT_DURATIONS_MS).reduce(
  (total, ms) => total + ms,
  0,
);

export const LAST_BEAT = 4;
